import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import type { View } from '../types';
import { VisuallyHidden } from './ui/visually-hidden';
import { 
  Share2, Navigation, X, MapPin, ChevronLeft, ChevronRight, 
  ZoomIn, ZoomOut, Info, ChevronDown, Image,
  StickyNote, ExternalLink
} from 'lucide-react';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getImageUrl } from '../lib/imageUtils';

// Fix NodeJS namespace for Timeout types

// Add resource_3 and resource_4 to the interface if not already in types.ts
interface ViewWithCredit extends View {
  resource_3?: string; // Credit Link
  resource_4?: string; // Credit Info
  notes?: string; // Custom user notes for this view
}

interface ViewModalMobileProps {
  view: ViewWithCredit | null;
  open: boolean;
  onClose: () => void;
  onShare: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onUpdateNotes?: (id: string, notes: string) => void; // New callback to save notes
}

export function ViewModalMobile({ 
  view, 
  open, 
  onClose, 
  onShare, 
  onNext, 
  onPrevious,
  onUpdateNotes
}: ViewModalMobileProps) {
  if (!view) return null;

  // Mobile-specific state
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Touch gesture state
  const [touchState, setTouchState] = useState({
    initialDistance: 0,
    initialZoom: 1,
    initialPosition: { x: 0, y: 0 },
    lastTap: 0,
    touches: [] as React.Touch[],
    isPinching: false
  });

  // Refs
  const imageRef = useRef<HTMLImageElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const startDragPos = useRef({ x: 0, y: 0 });
  const startMousePos = useRef({ x: 0, y: 0 });

  // Live mirrors of zoom/position - touchmove fires faster than React commits,
  // so reading state inside a gesture would use a stale value.
  const zoomRef = useRef(1);
  const positionRef = useRef({ x: 0, y: 0 });
  const maxZoom = 5;
  const zoomStep = 1.5; // multiplicative, so a press feels the same at 1x and at 4x

  // Touch gesture utility functions
  const getTouchDistance = (touch1: React.Touch, touch2: React.Touch): number => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTouchCenter = (touch1: React.Touch, touch2: React.Touch): { x: number; y: number } => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    };
  };

  // Reset view
  const resetView = useCallback(() => {
    zoomRef.current = 1;
    positionRef.current = { x: 0, y: 0 };
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Keep the live mirrors in step with state for changes that bypass zoomTo
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { positionRef.current = position; }, [position]);

  // Bound the pan so an image edge can reach the frame edge but never cross it.
  // Measured against the image's laid-out box, not the container, because
  // object-contain letterboxes it.
  const clampPan = useCallback((p: { x: number; y: number }, z: number) => {
    if (z <= 1.001) return { x: 0, y: 0 };

    const container = imageContainerRef.current;
    const image = imageRef.current;
    if (!container || !image) return p;

    const rect = container.getBoundingClientRect();
    const imageWidth = image.offsetWidth || rect.width;
    const imageHeight = image.offsetHeight || rect.height;

    // Symmetric difference, not max(0, ...) - see the note in ViewModal.tsx.
    // Lets the anchor be honoured across the letterbox margin instead of
    // locking the axis to centre whenever the image does not fill the frame.
    const maxX = Math.abs(imageWidth * z - rect.width) / 2;
    const maxY = Math.abs(imageHeight * z - rect.height) / 2;

    return {
      x: Math.min(maxX, Math.max(-maxX, p.x)),
      y: Math.min(maxY, Math.max(-maxY, p.y))
    };
  }, []);

  // Change the scale while holding one point still. Every path that changes
  // zoom goes through here - only the anchor differs (pinch midpoint, tap
  // point, frame centre). `from` lets a continuous gesture solve against the
  // state it started in, so error cannot accumulate across moves.
  const zoomTo = useCallback((
    clientX: number,
    clientY: number,
    nextZoom: number,
    from?: { zoom: number; position: { x: number; y: number } }
  ) => {
    const container = imageContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const baseZoom = from ? from.zoom : zoomRef.current;
    const basePosition = from ? from.position : positionRef.current;
    const z = Math.max(1, Math.min(maxZoom, nextZoom));

    if (z <= 1.001) {
      resetView();
      return;
    }

    const factor = z / baseZoom;
    const anchorX = Math.min(rect.width, Math.max(0, clientX - rect.left));
    const anchorY = Math.min(rect.height, Math.max(0, clientY - rect.top));
    const distX = anchorX - rect.width / 2 - basePosition.x;
    const distY = anchorY - rect.height / 2 - basePosition.y;

    const nextPosition = clampPan({
      x: basePosition.x + distX - distX * factor,
      y: basePosition.y + distY - distY * factor
    }, z);

    zoomRef.current = z;
    positionRef.current = nextPosition;
    setZoom(z);
    setPosition(nextPosition);
  }, [clampPan, maxZoom, resetView]);

  // A button press carries no touch point, so the anchor is the frame centre
  const zoomFromCentre = useCallback((nextZoom: number) => {
    const container = imageContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    zoomTo(rect.left + rect.width / 2, rect.top + rect.height / 2, nextZoom);
  }, [zoomTo]);

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    
    const touches = Array.from(e.touches);
    const now = Date.now();
    
    if (touches.length === 1) {
      // Single touch - check for double tap or start panning
      const touch = touches[0];
      const timeSinceLastTap = now - touchState.lastTap;
      
      if (timeSinceLastTap < 300 && timeSinceLastTap > 50) {
        // Double tap detected - toggle zoom
        handleDoubleTapZoom(touch);
      } else {
        // Start panning if zoomed
        if (zoom > 1) {
          setIsPanning(true);
          startDragPos.current = { x: position.x, y: position.y };
          startMousePos.current = { x: touch.clientX, y: touch.clientY };
        }
      }
      
      setTouchState(prev => ({
        ...prev,
        lastTap: now,
        touches
      }));
    } else if (touches.length === 2) {
      // Pinch gesture start
      const distance = getTouchDistance(touches[0], touches[1]);
      setTouchState(prev => ({
        ...prev,
        initialDistance: distance,
        initialZoom: zoom,
        initialPosition: { x: position.x, y: position.y },
        touches,
        isPinching: true
      }));
      setIsPanning(false);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    
    const touches = Array.from(e.touches);
    
    if (touches.length === 1 && isPanning && zoom > 1) {
      // Single touch panning
      const touch = touches[0];
      const deltaX = touch.clientX - startMousePos.current.x;
      const deltaY = touch.clientY - startMousePos.current.y;

      const nextPosition = clampPan({
        x: startDragPos.current.x + deltaX,
        y: startDragPos.current.y + deltaY
      }, zoom);

      positionRef.current = nextPosition;
      setPosition(nextPosition);
    } else if (touches.length === 2 && touchState.isPinching) {
      // Pinch to zoom, anchored on the midpoint between the fingers so the
      // pixel they straddle stays put. Solved against the state the gesture
      // started in rather than the previous frame.
      const distance = getTouchDistance(touches[0], touches[1]);

      if (touchState.initialDistance > 0) {
        const scale = distance / touchState.initialDistance;
        const centre = getTouchCenter(touches[0], touches[1]);

        zoomTo(centre.x, centre.y, touchState.initialZoom * scale, {
          zoom: touchState.initialZoom,
          position: touchState.initialPosition
        });
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touches = Array.from(e.touches);

    if (touches.length === 0) {
      setIsPanning(false);
      setTouchState(prev => ({
        ...prev,
        touches,
        isPinching: false,
        initialDistance: 0
      }));
    } else if (touches.length === 1) {
      // Went from 2 fingers to 1 - end pinch, potentially start pan
      setTouchState(prev => ({
        ...prev,
        touches,
        isPinching: false,
        initialDistance: 0
      }));
    } else {
      setTouchState(prev => ({
        ...prev,
        touches
      }));
    }
  };

  // Same stops as the desktop double-click: 1x -> 2x -> 4x -> fit
  const handleDoubleTapZoom = (touch: React.Touch) => {
    const next = [2, 4].find(stop => stop > zoomRef.current + 0.01);

    if (next === undefined) {
      resetView();
      return;
    }

    // Hold the tapped point still across the step
    zoomTo(touch.clientX, touch.clientY, next);
  };

  // Both figures come from the API (lib/api.ts), so the secondary unit is the
  // recorded mileage rather than a conversion of the kilometre value.
  const distance = { value: view.distanceKm, unit: 'km' };
  const otherUnit = 'mi';
  const otherValue = view.distanceMi.toFixed(2);

  // Status badge color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed': return 'bg-green-500';
      case 'likely': return 'bg-blue-500';
      case 'ifclear': return 'bg-cyan-500';
      case 'plausible': return 'bg-yellow-500';
      case 'dubious': return 'bg-orange-500';
      case 'unlikely': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  // Hide controls after user interaction
  useEffect(() => {
    if (!showControls) return;
    
    const timer = setTimeout(() => {
      setShowControls(false);
    }, 4000);
    
    return () => clearTimeout(timer);
  }, [showControls]);

  // Reset view when modal opens
  useEffect(() => {
    if (open) {
      resetView();
      setShowControls(true);
      setShowDetails(false);
    }
  }, [open, resetView]);

  return (
    <Dialog 
      open={open} 
      onOpenChange={(value) => !value && onClose()}
    >
      <DialogContent 
        className="w-screen h-screen max-w-none max-h-none p-0 border-none bg-black shadow-xl focus:outline-none outline-none rounded-none ring-0 ring-offset-0"
        style={{ margin: 0, transform: 'none', top: 0, left: 0 }}
      >
        <VisuallyHidden>
          <DialogTitle>
            View from {view.source} to {view.destination}
          </DialogTitle>
          <DialogDescription>
            Mobile view showing the view from {view.source} to {view.destination}, distance: {view.distance}. Use pinch to zoom, double-tap for 2x zoom, and drag to pan the image.
          </DialogDescription>
        </VisuallyHidden>

        {/* Main image container */}
        <div 
          ref={imageContainerRef}
          className="w-full h-full relative bg-black overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={() => setShowControls(true)}
        >
          {/* Image */}
          <div className="w-full h-full flex items-center justify-center">
            <img
              ref={imageRef}
              src={getImageUrl(view, 'full')}
              alt={`View from ${view.source} to ${view.destination}`}
              className="w-auto h-auto max-w-full max-h-full object-contain"
              style={{
                transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                transformOrigin: 'center',
                transition: (isPanning || touchState.isPinching) ? 'none' : 'transform 0.15s ease-out'
              }}
              onLoad={() => setImageLoaded(true)}
              draggable={false}
            />
          </div>

          {/* Mobile controls overlay */}
          <AnimatePresence>
            {showControls && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 pointer-events-none"
              >
                {/* Top bar */}
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 to-transparent p-4 pointer-events-auto">
                  <div className="flex items-center justify-between gap-2">
                    <button
                      onClick={onClose}
                      className="p-2 bg-black/50 rounded-full text-white flex-shrink-0"
                    >
                      <X className="h-6 w-6" />
                    </button>

                    <div className="flex-1 min-w-0 text-center px-2">
                      <h2 className="text-white text-sm font-medium truncate">
                        {view.source} → {view.destination}
                      </h2>
                      <p className="text-white/70 text-xs">
                        {distance.value} {distance.unit}
                      </p>
                    </div>

                    <button
                      onClick={() => setShowDetails(!showDetails)}
                      className="p-2 bg-black/50 rounded-full text-white flex-shrink-0"
                    >
                      <Info className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Navigation buttons */}
                {onPrevious && (
                  <button
                    onClick={onPrevious}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white pointer-events-auto"
                  >
                    <ChevronLeft className="h-8 w-8" />
                  </button>
                )}
                
                {onNext && (
                  <button
                    onClick={onNext}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white pointer-events-auto"
                  >
                    <ChevronRight className="h-8 w-8" />
                  </button>
                )}

                {/* Bottom controls */}
                <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/70 to-transparent p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] pointer-events-auto">
                  <div className="flex items-center justify-center space-x-4">
                    <button
                      onClick={() => zoomFromCentre(zoom / zoomStep)}
                      disabled={zoom <= 1}
                      className="p-3 bg-black/50 rounded-full text-white disabled:opacity-50"
                    >
                      <ZoomOut className="h-6 w-6" />
                    </button>
                    
                    {/* Persistent scale readout - tap to return to 100% */}
                    <button
                      onClick={resetView}
                      className="px-3 py-3 bg-black/50 rounded-full text-white text-sm font-medium tabular-nums min-w-[4rem]"
                      aria-label={`Zoom ${Math.round(zoom * 100)} percent. Reset to 100 percent`}
                    >
                      {Math.round(zoom * 100)}%
                    </button>

                    <button
                      onClick={() => zoomFromCentre(zoom * zoomStep)}
                      disabled={zoom >= maxZoom}
                      className="p-3 bg-black/50 rounded-full text-white disabled:opacity-50"
                    >
                      <ZoomIn className="h-6 w-6" />
                    </button>
                    
                    <button
                      onClick={onShare}
                      className="p-3 bg-black/50 rounded-full text-white"
                    >
                      <Share2 className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Gesture hints */}
                {zoom === 1 && (
                  <div className="absolute right-4 bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] bg-black/70 rounded-lg p-3 pointer-events-none">
                    <div className="text-white text-xs space-y-1">
                      <div>• Pinch to zoom</div>
                      <div>• Double-tap for 2x</div>
                      <div>• Drag to pan</div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Details panel */}
          <AnimatePresence>
            {showDetails && (
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                className="absolute bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-md border-t border-cyan-500/30 max-h-[70vh] overflow-y-auto"
              >
                <div className="p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))]">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white text-lg font-semibold">Details</h3>
                    <button
                      onClick={() => setShowDetails(false)}
                      className="p-1 text-white/70 hover:text-white"
                    >
                      <ChevronDown className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Distance and status */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-cyan-400 text-xs uppercase tracking-wide">Distance</p>
                        <p className="text-white text-xl font-bold">
                          {distance.value.toLocaleString()} {distance.unit}
                        </p>
                        <p className="text-white/70 text-sm">({otherValue} {otherUnit})</p>
                      </div>
                      <div className="text-right">
                        <p className="text-blue-400 text-xs uppercase tracking-wide">Status</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={cn("w-3 h-3 rounded-full", getStatusColor(view.status))} />
                          <span className="text-white capitalize">{view.status === 'ifclear' ? 'if clear' : view.status}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Source and Destination */}
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="h-4 w-4 text-cyan-400" />
                        <h4 className="text-white font-medium">Source</h4>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <p className="text-white font-medium">{view.source}</p>
                        <p className="text-white/70 text-sm">{view.state}, {view.country}</p>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Navigation className="h-4 w-4 text-blue-400" />
                        <h4 className="text-white font-medium">Destination</h4>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <p className="text-white font-medium">{view.destination}</p>
                        <p className="text-white/70 text-sm">
                          {view.destinationState}, {view.destinationCountry || view.country}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Credit information if available */}
                  {(view.resource_3 || view.resource_4) && (
                    <div className="mt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Info className="h-4 w-4 text-orange-400" />
                        <h4 className="text-white font-medium">Credits</h4>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        {view.resource_4 && (
                          <p className="text-white/90 text-sm mb-2">{view.resource_4}</p>
                        )}
                        {view.resource_3 && (
                          <a 
                            href={view.resource_3} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-orange-400 hover:text-orange-300 underline text-sm flex items-center gap-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            View Source
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Notes if available */}
                  {view.notes && (
                    <div className="mt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <StickyNote className="h-4 w-4 text-yellow-400" />
                        <h4 className="text-white font-medium">Notes</h4>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <p className="text-white/90 text-sm whitespace-pre-wrap">{view.notes}</p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
} 